import { mkdir, readFile, rm, writeFile } from "node:fs/promises";
import { homedir, tmpdir } from "node:os";
import { dirname, join } from "node:path";
import { launchSystemChrome, silentLogger } from "@million/react-stack-browser";
import { chromium, type Browser as PlaywrightBrowser, type Page } from "playwright";

export interface BrowserSessionState {
  pid: number;
  wsUrl: string;
  userDataDir: string;
  tempUserDataDir: string | undefined;
  startedAt: number;
}

const SESSION_DIR = join(homedir(), ".react-stack");
const SESSION_FILE = join(SESSION_DIR, "browser.json");
const CDP_CONNECT_TIMEOUT_MS = 10_000;

const isProcessAlive = (pid: number): boolean => {
  try {
    process.kill(pid, 0);
    return true;
  } catch (cause) {
    if (cause instanceof Error && "code" in cause && cause.code === "EPERM") return true;
    return false;
  }
};

const readSessionFile = async (): Promise<BrowserSessionState | undefined> => {
  try {
    const raw = await readFile(SESSION_FILE, "utf8");
    const parsed = JSON.parse(raw) as BrowserSessionState;
    if (typeof parsed.pid !== "number" || typeof parsed.wsUrl !== "string") return undefined;
    return parsed;
  } catch {
    return undefined;
  }
};

const writeSessionFile = async (state: BrowserSessionState): Promise<void> => {
  await mkdir(SESSION_DIR, { recursive: true });
  await writeFile(SESSION_FILE, JSON.stringify(state, null, 2), "utf8");
};

const deleteSessionFile = async (): Promise<void> => {
  await rm(SESSION_FILE, { force: true });
};

export const loadActiveSession = async (): Promise<BrowserSessionState | undefined> => {
  const state = await readSessionFile();
  if (!state) return undefined;
  if (!isProcessAlive(state.pid)) {
    await deleteSessionFile().catch(() => {});
    return undefined;
  }
  return state;
};

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  message: string,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((cause) => {
        clearTimeout(timer);
        reject(cause);
      });
  });

export const launchSession = async (): Promise<{
  state: BrowserSessionState;
  freshlyLaunched: true;
}> => {
  const profilePath = join(tmpdir(), `react-stack-browser-${process.pid}-${Date.now()}`);
  const chrome = await launchSystemChrome({
    headless: true,
    profilePath,
    detached: true,
    logger: silentLogger,
  });

  const state: BrowserSessionState = {
    pid: chrome.process.pid ?? -1,
    wsUrl: chrome.wsUrl,
    userDataDir: chrome.userDataDir,
    tempUserDataDir: chrome.userDataDir,
    startedAt: Date.now(),
  };

  await writeSessionFile(state);
  return { state, freshlyLaunched: true };
};

export interface ConnectedSession {
  browser: PlaywrightBrowser;
  state: BrowserSessionState;
  freshlyLaunched: boolean;
  disconnect: () => Promise<void>;
}

export const ensureSession = async (): Promise<ConnectedSession> => {
  const existing = await loadActiveSession();
  const { state, freshlyLaunched } = existing
    ? { state: existing, freshlyLaunched: false }
    : await launchSession();

  let browser: PlaywrightBrowser;
  try {
    browser = await withTimeout(
      chromium.connectOverCDP(state.wsUrl),
      CDP_CONNECT_TIMEOUT_MS,
      `Failed to connect to browser session at ${state.wsUrl} within ${CDP_CONNECT_TIMEOUT_MS}ms`,
    );
  } catch (cause) {
    await deleteSessionFile().catch(() => {});
    throw cause;
  }

  // HACK: a freshly launched Chrome lands its first tab on chrome://newtab/
  // (or chrome://welcome on first run), not about:blank. Without normalizing,
  // a bare `snapshot` against a fresh session would happily snapshot the
  // Chrome new-tab page (Web Store / Customize buttons) instead of erroring
  // with "no page open". Force about:blank so ensurePage's check is reliable.
  if (freshlyLaunched) {
    const contexts = browser.contexts();
    const context = contexts[0] ?? (await browser.newContext({ ignoreHTTPSErrors: true }));
    const pages = context.pages();
    const page = pages[0] ?? (await context.newPage());
    if (page.url() !== "about:blank") {
      await page.goto("about:blank").catch(() => {});
    }
  }

  return {
    browser,
    state,
    freshlyLaunched,
    disconnect: async () => {
      await browser.close().catch(() => {});
    },
  };
};

export interface EnsurePageOptions {
  url?: string;
  waitUntil?: "load" | "domcontentloaded" | "networkidle" | "commit";
}

export const ensurePage = async (
  browser: PlaywrightBrowser,
  options: EnsurePageOptions = {},
): Promise<Page> => {
  const contexts = browser.contexts();
  const context = contexts[0] ?? (await browser.newContext({ ignoreHTTPSErrors: true }));
  const pages = context.pages();
  const page = pages[0] ?? (await context.newPage());

  if (options.url && page.url() !== options.url) {
    await page.goto(options.url, { waitUntil: options.waitUntil ?? "load" });
  } else if (!options.url && page.url() === "about:blank") {
    throw new Error(
      "No page is open in the browser session. Pass a URL or run `react-stack browser start <url>` first.",
    );
  }

  return page;
};

export interface StopSessionResult {
  pid: number;
  wasRunning: boolean;
}

export const stopSession = async (): Promise<StopSessionResult> => {
  const state = await readSessionFile();
  if (!state) return { pid: -1, wasRunning: false };

  const wasRunning = isProcessAlive(state.pid);
  if (wasRunning) {
    try {
      process.kill(state.pid);
    } catch {
      // Process may have died between the alive check and the kill — fall through.
    }
  }

  if (state.tempUserDataDir) {
    await rm(state.tempUserDataDir, { recursive: true, force: true }).catch(() => {});
  }
  await deleteSessionFile().catch(() => {});

  return { pid: state.pid, wasRunning };
};

export const ensureProtocol = (rawUrl: string): string => {
  if (/^[a-z]+:\/\//i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith("localhost") || /^\d+\.\d+\.\d+\.\d+/.test(rawUrl)) {
    return `http://${rawUrl}`;
  }
  return `https://${rawUrl}`;
};

export const sessionFilePath = (): string => SESSION_FILE;

// Suppress unused-export warning; consumers may want to reach into the dir.
export const sessionDir = (): string => dirname(SESSION_FILE);
